/*
 * Which appearance the reader has chosen, and how it reaches the document.
 *
 * Three states, not a boolean. "Automatic" is a real choice and the default one: it is what
 * everybody gets until they say otherwise, and it has to keep following the device afterwards
 * rather than freezing to whatever the device happened to be on the day it was read.
 *
 * The whole mechanism is one attribute. `tokens.css` already defines light on `:root`, dark
 * inside `(prefers-color-scheme: dark)` guarded by `:not([data-theme="light"])`, and dark again
 * on an explicit `[data-theme="dark"]`. So:
 *
 *   - automatic -> no attribute at all, and the media query decides, live.
 *   - light     -> data-theme="light", which also disarms the media query's guard.
 *   - dark      -> data-theme="dark".
 *
 * Removing the attribute rather than writing `data-theme="auto"` is the point: there is no rule
 * for "auto" and there does not need to be one, because the absence of a choice is exactly what
 * the stylesheet already treats as "ask the device".
 *
 * The same three lines run twice: once from an inline script in <head> before the first paint,
 * and once from here when the sheet changes the answer. `fragment/head.html` carries that copy
 * and names this file; a module cannot do it, because by the time one has been fetched and
 * evaluated the page has already been painted in the wrong colour.
 */

export const APPEARANCE_KEY = "weplay.appearance";

/**
 * The same choice, where the server can see it.
 *
 * `localStorage` is the record; this is a copy of it for the one thing the stylesheet cannot do.
 * A `<picture>` can only ask `prefers-color-scheme`, which is the device and not this choice, so
 * an image that has a light and a dark version has no way to follow an explicit pick. The server
 * renders the right one instead, the way it already reads `TIMEZONE` to format a date.
 *
 * Only an explicit pick is written. "Automatic" removes it, because automatic IS
 * `prefers-color-scheme`: with no cookie the server falls back to the media query, which is the
 * correct answer for that case rather than a guess at it.
 */
const APPEARANCE_COOKIE = "APPEARANCE";

export type Appearance = "auto" | "light" | "dark";

const APPEARANCES: readonly Appearance[] = ["auto", "light", "dark"];

function isAppearance(value: string | null): value is Appearance {
  return value !== null && (APPEARANCES as readonly string[]).includes(value);
}

/**
 * The stored choice, or "auto".
 *
 * Guarded, because `localStorage` is not always there to be read: Safari throws on the property
 * itself in a blocked third-party frame rather than returning null, and a menu that cannot be
 * opened because a preference could not be read is worse than one that opens on the default.
 */
export function readAppearance(): Appearance {
  try {
    const stored = window.localStorage.getItem(APPEARANCE_KEY);

    return isAppearance(stored) ? stored : "auto";
  } catch {
    return "auto";
  }
}

export function writeAppearance(appearance: Appearance): void {
  writeAppearanceCookie(appearance);

  try {
    if (appearance === "auto") {
      window.localStorage.removeItem(APPEARANCE_KEY);

      return;
    }

    window.localStorage.setItem(APPEARANCE_KEY, appearance);
  } catch {
    /* A choice that cannot be stored still applies for this page, which is the useful half. */
  }
}

/**
 * Brings the cookie up to whatever is stored, for a reader who chose before the cookie existed and
 * has not touched the setting since. Cheap enough to run on every page: it writes the same value
 * the browser already holds.
 */
export function syncAppearanceCookie(): void {
  writeAppearanceCookie(readAppearance());
}

/** Outside the `try` above: a cookie is not `localStorage` and does not throw where that does. */
function writeAppearanceCookie(appearance: Appearance): void {
  const attributes = "path=/; SameSite=Strict; Secure";

  document.cookie =
    appearance === "auto"
      ? `${APPEARANCE_COOKIE}=; Max-Age=0; ${attributes}`
      : `${APPEARANCE_COOKIE}=${appearance}; Max-Age=31536000; ${attributes}`;
}

export function applyAppearance(appearance: Appearance): void {
  if (appearance === "auto") {
    delete document.documentElement.dataset.theme;

    return;
  }

  document.documentElement.dataset.theme = appearance;
}
